import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRightIcon, FolderIcon, FolderPlusIcon, PencilIcon, Trash2Icon } from 'lucide-react';

import { ApiError } from '@mds/shared/lib/api';
import { stamp } from '@mds/shared/lib/dates';
import type {
  AdminFile,
  AdminFileFolder,
  BulkFileResult,
  FileMimeType,
  FileUploadStatus,
} from '@mds/shared/types/file';
import IconButton from '@mds/shared/ui/icon-button';
import Select from '@mds/shared/ui/select';

import { useSession } from '@/auth/use-session';
import FileDialog from '@/components/files/file-dialog';
import FileThumb from '@/components/files/file-thumb';
import FileUploader from '@/components/files/file-uploader';
import FolderDialog, { type FolderDialogMode } from '@/components/files/folder-dialog';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, selectCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import { Table, Row, Cell } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import * as files from '@/files/files.api';
import {
  buildTree,
  destinationOptions,
  pathLabel,
  pathTo,
  ROOT_LABEL,
  toFolderId,
  toFolderValue,
} from '@/files/folders';
import { statusTone } from '@/files/status-tone';
import { ACCEPTED_TYPES, formatBytes } from '@/files/upload';
import { useImageBackfill } from '@/files/use-image-backfill';
import { fileTypeLabel } from '@/lib/labels';

const ALL = 'All';
const PAGE_SIZE = 25;
const STATUSES: FileUploadStatus[] = ['ready', 'pending', 'rejected'];

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The checkbox skin the other admin tables use. */
const checkboxCls = 'h-6 w-6 accent-brand-edge';

/**
 * A bulk result in words: what happened, then each file that did not and why.
 * Both can be true at once — four of five deleted, one kept by an expense.
 */
function describeBulk(
  result: BulkFileResult,
  done: string,
  names: ReadonlyMap<string, string>,
): { notice: string | null; error: string | null } {
  const notice =
    result.succeeded.length > 0 ? `${count(result.succeeded.length, 'file')} ${done}.` : null;
  const error =
    result.failed.length > 0
      ? `${count(result.failed.length, 'file')} could not be changed: ` +
        result.failed
          .map((failure) => `${names.get(failure.fileId) ?? 'a file'} — ${failure.message}`)
          .join('; ')
      : null;
  return { notice, error };
}

/**
 * The file manager.
 *
 * Folders organise the library; they carry no visibility of their own, and
 * search looks through all of them at once. The open folder lives in the URL
 * (`?folder=`), so the back button and a shared link both land in it.
 *
 * Deletion here is the soft kind: it blocks access at once but keeps the object
 * so a content manager can restore it. Only a super administrator can purge,
 * and only something already soft-deleted — which is why that control appears
 * where it does rather than beside every row.
 */
export default function FilesPage() {
  const queryClient = useQueryClient();
  const { can, is } = useSession();
  const mayManage = can('content.manage');
  const mayPurge = is('super_admin');

  const [params, setParams] = useSearchParams();
  const folderId = params.get('folder');

  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [mimeType, setMimeType] = useState<string>(ALL);
  const [uploadStatus, setUploadStatus] = useState<string>(ALL);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  /** The row it was opened from — drawn at once while the fresh copy loads. */
  const [opened, setOpened] = useState<AdminFile | null>(null);
  const [confirmPurge, setConfirmPurge] = useState<AdminFile | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [moveTarget, setMoveTarget] = useState<string | null>(null);
  const [folderDialog, setFolderDialog] = useState<FolderDialogMode | null>(null);
  const [confirmFolderDelete, setConfirmFolderDelete] = useState<AdminFileFolder | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      // The API takes up to 100 characters; past that, search on the first 100.
      setSearch(q.trim().slice(0, 100));
      setPage(1);
      setSelected(new Set());
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const searching = search.length > 0;

  // Under 'files' so the refresh after every action re-reads the counts too.
  const folders = useQuery({ queryKey: ['files', 'folders'], queryFn: files.listFolders });
  const tree = useMemo(() => buildTree(folders.data ?? []), [folders.data]);
  const current = folderId ? (tree.byId.get(folderId) ?? null) : null;
  const subfolders = searching ? [] : (tree.children.get(current?.id ?? null) ?? []);

  // A link to a folder that has since been deleted lands on the root, not on an empty page.
  useEffect(() => {
    if (folderId && folders.isSuccess && !tree.byId.has(folderId)) {
      setParams({}, { replace: true });
    }
  }, [folderId, folders.isSuccess, tree, setParams]);

  const list = useQuery({
    queryKey: ['files', 'list', { folderId, page, search, mimeType, uploadStatus, includeDeleted }],
    queryFn: () =>
      files.listFiles({
        page,
        limit: PAGE_SIZE,
        q: search || undefined,
        // A search looks through every folder; browsing shows one.
        folderId: searching ? undefined : (folderId ?? 'root'),
        mimeType: mimeType === ALL ? undefined : (mimeType as FileMimeType),
        uploadStatus: uploadStatus === ALL ? undefined : (uploadStatus as FileUploadStatus),
        includeDeleted,
      }),
  });

  // Re-read on its own: an upload can finish or be rejected, or someone else
  // can delete or restore the file, after the list page was fetched. Keyed
  // under 'files' so the refresh after every action re-reads it too.
  const single = useQuery({
    queryKey: ['files', 'one', opened?.id],
    queryFn: () => files.getFile(opened?.id as string),
    enabled: opened !== null,
    placeholderData: opened ?? undefined,
  });
  const detail = opened ? (single.data ?? opened) : null;

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const pagination = list.data?.pagination;
  const made = useImageBackfill(rows, mayManage);
  const previewOf = (file: AdminFile) => file.thumbnailUrl ?? made[file.id] ?? null;

  const selectable = (file: AdminFile) => mayManage && !file.deletedAt;
  const selectableRows = rows.filter(selectable);
  const selectedRows = selectableRows.filter((file) => selected.has(file.id));
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['files'] });

  /** Anything that changes which files are on screen starts a fresh selection. */
  const changeView = (apply: () => void) => {
    apply();
    setPage(1);
    setSelected(new Set());
  };

  const openFolder = (id: string | null) =>
    changeView(() => {
      setQ('');
      setSearch('');
      setNotice(null);
      setError(null);
      setParams(id ? { folder: id } : {});
    });

  const openFile = (file: AdminFile) => {
    setError(null);
    setOpened(file);
  };

  const toggle = (fileId: string) =>
    setSelected((held) => {
      const next = new Set(held);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });

  const act = async (file: AdminFile, run: () => Promise<unknown>, done: string) => {
    setBusyId(file.id);
    setError(null);
    setNotice(null);
    try {
      await run();
      await refresh();
      setOpened(null);
      setConfirmPurge(null);
      setNotice(done);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  /** Asks for a fresh signed URL each time — they are short-lived by design. */
  const download = async (file: AdminFile) => {
    setBusyId(file.id);
    setError(null);
    try {
      const { url } = await files.downloadUrl(file.id);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The download could not be started.');
    } finally {
      setBusyId(null);
    }
  };

  const runBulk = async (run: (ids: string[]) => Promise<BulkFileResult>, done: string) => {
    const ids = selectedRows.map((file) => file.id);
    const names = new Map(selectedRows.map((file) => [file.id, file.originalFilename]));
    setBulkBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await run(ids);
      // What failed stays ticked, so it can be dealt with and retried.
      setSelected(new Set(result.failed.map((failure) => failure.fileId)));
      const summary = describeBulk(result, done, names);
      setNotice(summary.notice);
      setError(summary.error);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work. Try again.');
    } finally {
      setBulkBusy(false);
      setConfirmBulkDelete(false);
      setMoveTarget(null);
    }
  };

  const deleteFolder = async (folder: AdminFileFolder) => {
    setBulkBusy(true);
    setError(null);
    setNotice(null);
    try {
      await files.deleteFolder(folder.id);
      setConfirmFolderDelete(null);
      setNotice(`Folder ${folder.name} deleted.`);
      await refresh();
    } catch (e) {
      setConfirmFolderDelete(null);
      setError(e instanceof ApiError ? e.message : 'The folder could not be deleted. Try again.');
    } finally {
      setBulkBusy(false);
    }
  };

  /** What still sits in a folder, which is what stops it being deleted. */
  const contentsOf = (folder: AdminFileFolder) => {
    const folderCount = tree.children.get(folder.id)?.length ?? 0;
    return {
      folderCount,
      empty: folder.fileCount === 0 && folderCount === 0,
      words: [
        folder.fileCount > 0 && count(folder.fileCount, 'file'),
        folderCount > 0 && count(folderCount, 'folder'),
      ]
        .filter(Boolean)
        .join(' and '),
    };
  };
  const folderToDelete = confirmFolderDelete ? contentsOf(confirmFolderDelete) : null;

  const crumbs = [{ id: null as string | null, name: ROOT_LABEL }, ...pathTo(tree, folderId)];
  const here = searching ? 'Search results' : (current?.name ?? ROOT_LABEL);
  const filtered = searching || mimeType !== ALL || uploadStatus !== ALL;

  return (
    <div className="flex flex-col gap-6">
      {mayManage && (
        <Card title="Upload">
          <FileUploader
            folderId={current?.id ?? null}
            destination={pathLabel(tree, current?.id ?? null)}
            onUploaded={refresh}
          />
        </Card>
      )}

      <Card
        title={pagination ? `${here} · ${count(pagination.total, 'file')}` : here}
        action={
          <Toolbar>
            <input
              placeholder="Search every folder"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search files in every folder"
              className={controlCls + ' w-[240px]'}
            />
            <Select
              value={mimeType}
              onChange={(next) => changeView(() => setMimeType(next))}
              options={[
                { value: ALL, label: 'All types' },
                ...ACCEPTED_TYPES.map((t) => ({ value: t, label: fileTypeLabel(t) })),
              ]}
              aria-label="Filter by type"
            />
            <Select
              value={uploadStatus}
              onChange={(next) => changeView(() => setUploadStatus(next))}
              options={[ALL, ...STATUSES].map((s) => ({ value: s, label: s }))}
              aria-label="Filter by status"
            />
            <label className="flex items-center gap-2.5 text-base">
              <input
                type="checkbox"
                className={checkboxCls}
                checked={includeDeleted}
                onChange={(e) => changeView(() => setIncludeDeleted(e.target.checked))}
              />
              Show deleted
            </label>
            {mayManage && !searching && (
              <Button
                variant="quiet"
                onClick={() => setFolderDialog({ kind: 'create', parentId: current?.id ?? null })}
              >
                <FolderPlusIcon className="size-5" aria-hidden="true" />
                New folder
              </Button>
            )}
          </Toolbar>
        }
      >
        <nav
          aria-label="Folder path"
          className="mb-5 flex flex-wrap items-center gap-1.5 text-base"
        >
          {searching ? (
            <span className="text-ink-muted">
              Searching every folder.{' '}
              <button
                type="button"
                onClick={() => setQ('')}
                className="font-semibold text-brand-text underline-offset-4 hover:underline focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
              >
                Back to {current?.name ?? ROOT_LABEL}
              </button>
            </span>
          ) : (
            crumbs.map((crumb, at) => (
              <Fragment key={crumb.id ?? 'root'}>
                {at > 0 && (
                  <ChevronRightIcon className="size-4 text-ink-muted" aria-hidden="true" />
                )}
                {at === crumbs.length - 1 ? (
                  <span aria-current="page" className="font-semibold">
                    {crumb.name}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => openFolder(crumb.id)}
                    className="rounded-md font-semibold text-brand-text underline-offset-4 hover:underline focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
                  >
                    {crumb.name}
                  </button>
                )}
              </Fragment>
            ))
          )}
        </nav>

        {notice && (
          <p
            role="status"
            className="mb-4 rounded-xl border border-brand-edge/40 bg-brand-wash px-4 py-3 text-base"
          >
            {notice}
          </p>
        )}
        {error && !detail && (
          <p
            role="alert"
            className="mb-4 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
          >
            {error}
          </p>
        )}

        {selectedRows.length > 0 && (
          <div
            role="region"
            aria-label="Selected files"
            className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-brand-edge/40 bg-brand-wash px-4 py-3"
          >
            <span className="mr-auto text-base font-semibold">
              {count(selectedRows.length, 'file')} selected
            </span>
            <Button
              variant="quiet"
              disabled={bulkBusy}
              onClick={() => setMoveTarget(toFolderValue(current?.id ?? null))}
            >
              Move to…
            </Button>
            <Button variant="danger" disabled={bulkBusy} onClick={() => setConfirmBulkDelete(true)}>
              Delete
            </Button>
            <Button variant="quiet" disabled={bulkBusy} onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        )}

        {list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading files…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load files"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 && (subfolders.length === 0 || page > 1) ? (
          filtered || includeDeleted ? (
            <EmptyState
              title="No files match"
              hint={
                searching
                  ? 'No file in any folder has that in its name.'
                  : 'Try clearing the type and status filters.'
              }
            />
          ) : (
            <EmptyState
              title={current ? `${current.name} is empty` : 'No files yet'}
              hint={
                mayManage
                  ? 'Upload files here, or tick files in another folder and move them in.'
                  : undefined
              }
            />
          )
        ) : (
          <>
            <Table
              head={[
                ...(mayManage
                  ? [
                      <input
                        key="all"
                        type="checkbox"
                        className={checkboxCls}
                        checked={allSelected}
                        disabled={selectableRows.length === 0}
                        ref={(box) => {
                          if (box) {
                            box.indeterminate = selectedRows.length > 0 && !allSelected;
                          }
                        }}
                        onChange={() =>
                          setSelected(
                            allSelected ? new Set() : new Set(selectableRows.map((f) => f.id)),
                          )
                        }
                        aria-label="Select every file on this page"
                      />,
                    ]
                  : []),
                'Name',
                'Type',
                'Size',
                'Visibility',
                'Status',
                'Added',
                '',
              ]}
            >
              {page === 1 &&
                subfolders.map((folder) => {
                  const inside = contentsOf(folder).folderCount;
                  return (
                    <Row key={folder.id} onClick={() => openFolder(folder.id)}>
                      {mayManage && <Cell>{null}</Cell>}
                      <Cell>
                        <span className="flex items-center gap-3">
                          <span className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-brand-edge/35 bg-brand-wash text-brand-strong">
                            <FolderIcon className="size-5" strokeWidth={2} aria-hidden="true" />
                          </span>
                          <span className="font-semibold">{folder.name}</span>
                        </span>
                      </Cell>
                      <Cell>Folder</Cell>
                      <Cell className="text-ink-muted">
                        {count(folder.fileCount, 'file')}
                        {inside > 0 && ` · ${count(inside, 'folder')}`}
                      </Cell>
                      <Cell>{null}</Cell>
                      <Cell>{null}</Cell>
                      <Cell className="font-mono text-md">{stamp(folder.createdAt)}</Cell>
                      <Cell>
                        {mayManage && (
                          <span className="flex justify-end gap-2">
                            <IconButton
                              label={`Rename or move ${folder.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFolderDialog({ kind: 'edit', folder });
                              }}
                            >
                              <PencilIcon className="size-5" strokeWidth={2.2} aria-hidden="true" />
                            </IconButton>
                            <IconButton
                              label={`Delete ${folder.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmFolderDelete(folder);
                              }}
                            >
                              <Trash2Icon className="size-5" strokeWidth={2.2} aria-hidden="true" />
                            </IconButton>
                          </span>
                        )}
                      </Cell>
                    </Row>
                  );
                })}

              {rows.map((file) => (
                <Row
                  key={file.id}
                  onClick={() => openFile(file)}
                  selected={selected.has(file.id) && selectable(file)}
                >
                  {mayManage && (
                    <Cell>
                      {selectable(file) && (
                        // The row opens the file; the box only ticks it.
                        <label className="flex" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className={checkboxCls}
                            checked={selected.has(file.id)}
                            onChange={() => toggle(file.id)}
                            aria-label={`Select ${file.originalFilename}`}
                          />
                        </label>
                      )}
                    </Cell>
                  )}
                  <Cell>
                    <span className="flex items-center gap-3">
                      <FileThumb url={previewOf(file)} mimeType={file.mimeType} />
                      <span className="min-w-0">
                        <span
                          className={
                            'block font-semibold break-words ' +
                            (file.deletedAt ? 'line-through opacity-70' : '')
                          }
                        >
                          {file.originalFilename}
                        </span>
                        {searching && (
                          <span className="block text-sm text-ink-muted">
                            in {pathLabel(tree, file.folderId)}
                          </span>
                        )}
                      </span>
                    </span>
                  </Cell>
                  <Cell>{fileTypeLabel(file.mimeType)}</Cell>
                  <Cell className="font-mono text-md tabular-nums">
                    {formatBytes(file.sizeBytes)}
                  </Cell>
                  <Cell>
                    <Chip>{file.visibility}</Chip>
                  </Cell>
                  <Cell>
                    <Chip className={'border ' + statusTone(file)}>
                      {file.deletedAt ? 'deleted' : file.uploadStatus}
                    </Chip>
                  </Cell>
                  <Cell className="font-mono text-md">{stamp(file.createdAt)}</Cell>
                  <Cell>
                    {file.uploadStatus === 'ready' && !file.deletedAt && (
                      <Button
                        variant="quiet"
                        disabled={busyId === file.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          void download(file);
                        }}
                      >
                        Download
                      </Button>
                    )}
                  </Cell>
                </Row>
              ))}
            </Table>

            <Pager
              pagination={pagination}
              onPage={(next) => {
                setPage(next);
                setSelected(new Set());
              }}
            />
          </>
        )}
      </Card>

      {detail && (
        <FileDialog
          key={detail.id}
          file={detail}
          thumbnailUrl={previewOf(detail)}
          tree={tree}
          mayManage={mayManage}
          mayPurge={mayPurge}
          busy={busyId === detail.id}
          actionError={error}
          onClose={() => setOpened(null)}
          onSaved={async (updated) => {
            setOpened(null);
            setNotice(`Saved ${updated.originalFilename}.`);
            await refresh();
          }}
          onDownload={() => void download(detail)}
          onDelete={() =>
            void act(
              detail,
              () => files.softDelete(detail.id),
              `${detail.originalFilename} deleted. Turn on Show deleted to restore it.`,
            )
          }
          onRestore={() =>
            void act(detail, () => files.restore(detail.id), `${detail.originalFilename} restored.`)
          }
          onPurge={() => setConfirmPurge(detail)}
        />
      )}

      <Modal
        open={moveTarget !== null}
        onClose={() => setMoveTarget(null)}
        title={`Move ${count(selectedRows.length, 'file')}`}
        footer={
          moveTarget !== null ? (
            <>
              <Button
                disabled={
                  bulkBusy || selectedRows.every((file) => file.folderId === toFolderId(moveTarget))
                }
                onClick={() => {
                  const folderId = toFolderId(moveTarget);
                  void runBulk(
                    (ids) => files.bulkMove(ids, folderId),
                    `moved to ${pathLabel(tree, folderId)}`,
                  );
                }}
              >
                {bulkBusy ? 'Moving…' : 'Move'}
              </Button>
              <Button variant="quiet" onClick={() => setMoveTarget(null)}>
                Cancel
              </Button>
            </>
          ) : null
        }
      >
        <Field label="Move to">
          <Select
            variant="field"
            className={selectCls}
            value={moveTarget ?? undefined}
            onChange={setMoveTarget}
            options={destinationOptions(tree)}
          />
        </Field>
        <p className="m-0 text-base text-ink-muted">
          Moving changes only where a file is listed. Its link, visibility and anywhere it is used
          stay the same.
        </p>
      </Modal>

      <Modal
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        title={`Delete ${count(selectedRows.length, 'file')}?`}
        footer={
          <>
            <Button
              variant="danger"
              disabled={bulkBusy}
              onClick={() => void runBulk(files.bulkDelete, 'deleted')}
            >
              {bulkBusy ? 'Deleting…' : 'Delete'}
            </Button>
            <Button variant="quiet" onClick={() => setConfirmBulkDelete(false)}>
              Cancel
            </Button>
          </>
        }
      >
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-base">
          {selectedRows.slice(0, 5).map((file) => (
            <li key={file.id} className="font-semibold break-words text-ink">
              {file.originalFilename}
            </li>
          ))}
          {selectedRows.length > 5 && (
            <li className="text-ink-muted">and {count(selectedRows.length - 5, 'more file')}</li>
          )}
        </ul>
        <p className="m-0 text-lg">
          They stop being available at once, including on any public page that shows them. Turn on
          Show deleted to find and restore them later.
        </p>
      </Modal>

      {folderDialog && (
        <FolderDialog
          key={folderDialog.kind === 'edit' ? folderDialog.folder.id : 'new'}
          mode={folderDialog}
          tree={tree}
          onClose={() => setFolderDialog(null)}
          onSaved={async (folder) => {
            setFolderDialog(null);
            setError(null);
            setNotice(
              folderDialog.kind === 'create'
                ? `Folder ${folder.name} created.`
                : `Folder ${folder.name} saved.`,
            );
            await refresh();
          }}
        />
      )}

      <Modal
        open={!!confirmFolderDelete}
        onClose={() => setConfirmFolderDelete(null)}
        title={`Delete ${confirmFolderDelete?.name ?? 'folder'}?`}
        footer={
          confirmFolderDelete && folderToDelete ? (
            <>
              {folderToDelete.empty && (
                <Button
                  variant="danger"
                  disabled={bulkBusy}
                  onClick={() => void deleteFolder(confirmFolderDelete)}
                >
                  {bulkBusy ? 'Deleting…' : 'Delete folder'}
                </Button>
              )}
              <Button variant="quiet" onClick={() => setConfirmFolderDelete(null)}>
                {folderToDelete.empty ? 'Cancel' : 'Close'}
              </Button>
            </>
          ) : null
        }
      >
        {confirmFolderDelete && folderToDelete && (
          <p className="m-0 text-lg">
            {folderToDelete.empty ? (
              <>
                The folder is empty, so nothing else changes. Deleted files that were in it move to
                the top level, where Show deleted still finds them.
              </>
            ) : (
              <>
                Only an empty folder can be deleted, and <strong>{confirmFolderDelete.name}</strong>{' '}
                still holds {folderToDelete.words}. Move or delete what is inside it first.
              </>
            )}
          </p>
        )}
      </Modal>

      <Modal
        open={!!confirmPurge}
        onClose={() => setConfirmPurge(null)}
        title="Purge permanently?"
        footer={
          confirmPurge ? (
            <>
              <Button
                variant="danger"
                disabled={busyId === confirmPurge.id}
                onClick={() =>
                  void act(
                    confirmPurge,
                    () => files.purge(confirmPurge.id),
                    `${confirmPurge.originalFilename} purged.`,
                  )
                }
              >
                {busyId === confirmPurge.id ? 'Purging…' : 'Purge permanently'}
              </Button>
              <Button variant="quiet" onClick={() => setConfirmPurge(null)}>
                Cancel
              </Button>
            </>
          ) : null
        }
      >
        <p className="m-0 text-lg">
          <strong>{confirmPurge?.originalFilename}</strong> and its stored object will be deleted
          for good. This cannot be undone, and nothing restores it afterwards.
        </p>
      </Modal>
    </div>
  );
}
